import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import { getFileTypeInfo, formatFileSize, truncateFileName } from '../../utils/fileUtils';
import { useAuth } from '../../hooks/useAuth';
import { getDomainName } from '../../utils/linkUtils';
import './BoardDetail.css';

const BoardDetail: React.FC = () => {
  const { category, id } = useParams<{ category: string; id: string }>();
  const navigate = useNavigate();
  const [board, setBoard] = useState<any>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [files, setFiles] = useState<any[]>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [newComment, setNewComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editingComment, setEditingComment] = useState('');
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [expandedComments, setExpandedComments] = useState<Set<number>>(new Set());
  const { isAdmin } = useAuth();

  const fetchComments = useCallback(async () => {
    if (id) {
      try {
        const res = await axiosInstance.get(`/api/boards/comments/${id}`);
        setComments(res.data);
      } catch (err) {
        console.error('댓글 로딩 실패:', err);
      }
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      axiosInstance.get(`/api/boards/${id}`).then(res => setBoard(res.data));
      fetchComments();
      axiosInstance.get(`/api/boards/attach/${id}`).then(res => setFiles(res.data));
      axiosInstance.get('/api/auth/me').then(res => setCurrentUser(res.data)).catch(err => {
        // 로그인하지 않은 경우 무시
      });
    }
  }, [id, fetchComments]);

  // 실시간 댓글 업데이트 수신
  useEffect(() => {
    const handleCommentChange = (e: Event) => {
      const customEvent = e as CustomEvent;
      const { contentType, boardId } = customEvent.detail;
      if (contentType === 'board' && String(boardId) === String(id)) {
        fetchComments();
      }
    };

    window.addEventListener('comment_change', handleCommentChange);
    return () => {
      window.removeEventListener('comment_change', handleCommentChange);
    };
  }, [id, fetchComments]);

  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    
    try {
      const res = await axiosInstance.post('/api/boards/comments', {
        board_id: id,
        comment: newComment
      });
      
      const commentsRes = await axiosInstance.get(`/api/boards/comments/${id}`);
      setComments(commentsRes.data);
      setNewComment('');
    } catch (err: any) {
      if (err.response?.status === 401) {
        alert('로그인이 필요합니다.');
        navigate('/login');
      } else {
        alert('댓글 작성에 실패했습니다.');
      }
    }
  };

  const handleBackToList = () => {
    navigate(`/boards/${category}`);
  };

  const handleEdit = () => {
    navigate(`/boards/${category}/${id}/edit`);
  };

  // 댓글 수정
  const handleEditComment = (comment: any) => {
    setEditingCommentId(comment.id);
    setEditingComment(comment.comment);
  };
  
  const handleUpdateComment = async (commentId: number) => {
    try {
      await axiosInstance.put(`/api/boards/comments/${commentId}`, {
        comment: editingComment
      });
      const commentsRes = await axiosInstance.get(`/api/boards/comments/${id}`);
      setComments(commentsRes.data);
      setEditingCommentId(null);
      setEditingComment('');
    } catch (err) {
      alert('댓글 수정에 실패했습니다.');
    }
  };
  
  // 댓글 삭제
  const handleDeleteComment = async (commentId: number) => {
    if (!window.confirm('댓글을 삭제하시겠습니까?')) return;
    try {
      await axiosInstance.delete(`/api/boards/comments/${commentId}`);
      const commentsRes = await axiosInstance.get(`/api/boards/comments/${id}`);
      setComments(commentsRes.data);
    } catch (err) {
      alert('댓글 삭제에 실패했습니다.');
    }
  };
  
  // 답글 작성
  const handleReply = async (parentId: number) => {
    if (!replyContent.trim()) return;
    try {
      await axiosInstance.post('/api/boards/comments', {
        board_id: id,
        parent_id: parentId,
        comment: replyContent.trim()
      });
      const commentsRes = await axiosInstance.get(`/api/boards/comments/${id}`);
      setComments(commentsRes.data);
      setReplyingTo(null);
      setReplyContent('');
    } catch (err) {
      alert('답글 작성에 실패했습니다.');
    }
  };

  // 답글(계층) 토글
  const toggleReplies = (commentId: number) => {
    setExpandedComments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(commentId)) {
        newSet.delete(commentId);
      } else {
        newSet.add(commentId);
      }
      return newSet;
    });
  };

  // 공지 고정/해제 핸들러
  const handlePinToggle = async () => {
    if (!board) return;
    const nextPinned = !board.is_pinned;

    try {
      await axiosInstance.put(`/api/boards/${board.id}`, { is_pinned: nextPinned });
      setBoard((prev: any) => prev ? { ...prev, is_pinned: nextPinned } : prev);
      alert(nextPinned ? '공지로 설정되었습니다.' : '공지 고정이 해제되었습니다.');
    } catch (err) {
      alert('공지 고정 상태 변경에 실패했습니다.');
    }
  };

  if (!board) return <div style={{ padding: 40, textAlign: 'center', fontWeight: 'bold' }}>Loading...</div>;

  const isAuthor = currentUser && currentUser.id === board.user_id;

  // 트리 구조 렌더링 (컨텐츠 상세화면 스타일 적용)
  const renderComment = (comment: any, depth: number = 0) => {
    const replies = comments.filter(c => c.parent_id === comment.id);
    const isCommentAuthor = currentUser && currentUser.id === comment.user_id;
    const isCurrentUserAdmin = currentUser && currentUser.role === 'admin';
    const isExpanded = expandedComments.has(comment.id);
    const created = new Date(comment.created_at);
    const updated = comment.updated_at ? new Date(comment.updated_at) : null;
    const isEdited = updated && updated > created;
    const isEditing = editingCommentId === comment.id;
    
    return (
      <div key={comment.id} className="comment-node-wrapper" style={{ marginLeft: depth * 24 }}>
        {depth > 0 && <div className="comment-indent-line" />}
        
        <div className="comment-card" style={{ marginBottom: 12 }}>
          <div className="comment-card-header">
            <div className="comment-user-info">
              {replies.length > 1 && (
                <button
                  type="button"
                  onClick={() => toggleReplies(comment.id)}
                  className="comment-toggle-btn"
                >
                  <svg 
                    className={`comment-chevron-svg ${isExpanded ? 'expanded' : ''}`} 
                    viewBox="0 0 24 24" 
                    width="14" 
                    height="14"
                  >
                    <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" fill="currentColor" />
                  </svg>
                  <span>{replies.length}</span>
                </button>
              )}
              <span className="comment-author-name">{comment.display_name || '익명'}</span>
            </div>
            
            <div className="comment-meta-info">
              <span className="comment-created-time">{created.toLocaleString()}</span>
              {isEdited && <span className="comment-edited-label">(수정됨)</span>}
            </div>
          </div>
          
          {isEditing ? (
            <textarea
              value={editingComment}
              onChange={e => setEditingComment(e.target.value)}
              className="comment-textarea-box"
              style={{ minHeight: 60, marginBottom: 8 }}
            />
          ) : (
            <p className="comment-body-content">{comment.comment}</p>
          )}
          
          {/* 답글 작성 버튼을 본문 아래에 분리 */}
          <div className="comment-actions-bar">
            {currentUser && (
              <div>
                {replyingTo !== comment.id && (
                  <button 
                    type="button" 
                    onClick={() => setReplyingTo(comment.id)} 
                    className="comment-action-btn-chip reply"
                  >
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 17 4 12 9 7"></polyline>
                      <path d="M20 18v-2a4 4 0 0 0-4-4H4"></path>
                    </svg>
                    <span>답글 작성</span>
                  </button>
                )}
              </div>
            )}
            
            {(isCommentAuthor || isCurrentUserAdmin) && (
              <div className="comment-author-actions">
                {isEditing ? (
                  <>
                    <button 
                      type="button" 
                      onClick={() => handleUpdateComment(comment.id)} 
                      className="comment-action-btn-chip save"
                    >
                      저장
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setEditingCommentId(null)} 
                      className="comment-action-btn-chip cancel"
                    >
                      취소
                    </button>
                  </>
                ) : (
                  <>
                    <button 
                      type="button" 
                      onClick={() => handleEditComment(comment)} 
                      className="comment-action-btn-chip edit"
                    >
                      <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                      <span>수정</span>
                    </button>
                    <button 
                      type="button" 
                      onClick={() => handleDeleteComment(comment.id)} 
                      className="comment-action-btn-chip delete"
                    >
                      <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                      </svg>
                      <span>삭제</span>
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
          
          {replyingTo === comment.id && (
            <div className="reply-input-box">
              <textarea
                value={replyContent}
                onChange={e => setReplyContent(e.target.value)}
                placeholder="답글을 입력하세요"
                className="comment-textarea-box"
                style={{ minHeight: 40, marginBottom: 8 }}
              />
              <div className="reply-actions-row">
                <button 
                  type="button" 
                  onClick={() => handleReply(comment.id)} 
                  className="comment-action-btn-chip save"
                >
                  등록
                </button>
                <button 
                  type="button" 
                  onClick={() => setReplyingTo(null)} 
                  className="comment-action-btn-chip cancel"
                >
                  취소
                </button>
              </div>
            </div>
          )}
        </div>
        {/* 답글 재귀 렌더링 (펼침 상태일 때만) */}
        {replies.length > 0 && (replies.length === 1 || isExpanded) &&
          replies.map(reply => renderComment(reply, depth + 1))}
      </div>
    );
  };

  return (
    <div className="board-detail-container">
      <div className="board-detail-header">
        <button 
          onClick={handleBackToList}
          className="comment-action-btn-chip cancel"
          style={{ padding: '8px 16px', fontSize: '13px' }}
        >
          목록으로
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          {isAuthor && (
            <button 
              onClick={handleEdit}
              className="comment-action-btn-chip save"
              style={{ padding: '8px 16px', fontSize: '13px', backgroundColor: '#007bff', borderColor: '#007bff' }}
            >
              수정
            </button>
          )}
          {/* 공지 고정/해제 버튼: 관리자만 노출 */}
          {isAdmin() && board && (
            <button
              onClick={handlePinToggle}
              className="comment-action-btn-chip save"
              style={{
                padding: '8px 16px',
                fontSize: '13px',
                backgroundColor: board.is_pinned ? '#dc3545' : '#6c757d',
                borderColor: board.is_pinned ? '#dc3545' : '#6c757d'
              }}
            >
              {board.is_pinned ? '[공지 고정 해제]' : '[공지 고정하기]'}
            </button>
          )}
        </div>
      </div>

      <h2 className="board-detail-title">{board.title}</h2>
      
      <div className="board-detail-meta">
        ⏱️ 작성일: {new Date(board.created_at).toLocaleString()}
        {board.is_pinned && <span style={{ color: '#dc3545', fontWeight: 'bold', marginLeft: 8 }}>[공지]</span>}
      </div>
      
      <p className="board-detail-content">{board.content}</p>

      {board.lnks && (
        <div className="links-section">
          <div className="links-title">
            🌐 관련 링크
          </div>
          <div className="links-grid">
            {board.lnks.split(';').filter(Boolean).map((lnk: string, i: number) => {
              const displayDomain = getDomainName(lnk);
              return (
                <a 
                  key={i} 
                  href={lnk} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="external-link-chip"
                  title={lnk}
                >
                  🌐 {displayDomain}
                  <svg className="external-svg-icon" viewBox="0 0 24 24" width="12" height="12">
                    <path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" fill="currentColor" />
                  </svg>
                </a>
              );
            })}
          </div>
        </div>
      )}

      {files.length > 0 && (
        <div className="board-attach-section">
          <strong className="board-attach-title">📎 첨부파일</strong>
          <ul className="board-attach-list">
            {files.map(file => {
              const isLabel = !file.file_type?.includes('/') && !file.file_type?.includes('application');
              const fileInfo = isLabel 
                ? (() => {
                    const labelIconMap: Record<string, string> = {
                      'Word 문서': '📄',
                      'Excel 문서': '📊', 
                      'PowerPoint 문서': '📽️',
                      'PDF 문서': '📋',
                      '이미지': '🖼️',
                      '비디오': '🎬',
                      '오디오': '🎵',
                      '압축 파일': '📦',
                      '텍스트': '📝'
                    };
                    const icon = labelIconMap[file.file_type] || '📎';
                    const colorMap: Record<string, string> = {
                      'Word 문서': '#2b579a',
                      'Excel 문서': '#217346',
                      'PowerPoint 문서': '#d24726',
                      'PDF 문서': '#dc3545',
                      '이미지': '#6f42c1',
                      '비디오': '#fd7e14',
                      '오디오': '#20c997',
                      '압축 파일': '#6c757d',
                      '텍스트': '#495057'
                    };
                    const color = colorMap[file.file_type] || '#007bff';
                    return { icon, label: file.file_type, color };
                  })()
                : getFileTypeInfo(file.file_name, file.file_type);
              
              return (
                <li key={file.id}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    padding: '8px 12px', 
                    backgroundColor: 'white', 
                    border: '1px solid #e9ecef', 
                    borderRadius: 6,
                    transition: 'all 0.2s ease'
                  }}>
                    <span style={{ fontSize: '20px', marginRight: 8 }}>
                      {fileInfo.icon}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <a 
                        href={`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}${file.file_url}`}
                        download={file.file_name}
                        style={{ 
                          color: fileInfo.color, 
                          textDecoration: 'none',
                          fontWeight: '600',
                          display: 'block',
                          fontSize: '13.5px'
                        }}
                        title={file.file_name}
                      >
                        {truncateFileName(file.file_name, 40)}
                      </a>
                      <div style={{ 
                        fontSize: '12px', 
                        color: '#6c757d', 
                        marginTop: 2,
                        display: 'flex',
                        justifyContent: 'space-between'
                      }}>
                        <span>{fileInfo.label}</span>
                        {file.file_size && (
                          <span>{formatFileSize(file.file_size)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* 댓글 섹션 */}
      <div className="comments-section">
        <h3 className="comments-header-title">💬 댓글 ({comments.length})</h3>
        
        <form onSubmit={handleCommentSubmit} className="comment-form-container">
          <textarea
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            placeholder={currentUser ? "댓글을 작성해주세요..." : "로그인 후 댓글을 작성할 수 있습니다."}
            disabled={!currentUser}
            className="comment-textarea-box"
          />
          <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
            <button 
              type="submit"
              disabled={!currentUser || !newComment.trim()}
              className="comment-action-btn-chip save"
              style={{
                padding: '8px 20px',
                fontSize: '14.5px',
                backgroundColor: currentUser && newComment.trim() ? '#28a745' : '#6c757d',
                borderColor: currentUser && newComment.trim() ? '#28a745' : '#6c757d',
                cursor: currentUser && newComment.trim() ? 'pointer' : 'not-allowed'
              }}
            >
              댓글 작성
            </button>
          </div>
        </form>

        <div className="comments-list-container">
          {comments.filter(c => !c.parent_id).length === 0 ? (
            <div className="no-comments-placeholder">
              아직 댓글이 없습니다.
            </div>
          ) : (
            comments.filter(c => !c.parent_id).map(comment => renderComment(comment))
          )}
        </div>
      </div>
    </div>
  );
};

export default BoardDetail;
